import { NodeSelection, Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

const SELECTED_NODE_CLASS = "ProseMirror-selectednode";

/**
 * Marks every `typeName` atom that a range selection covers as selected.
 *
 * An inline atom draws no text, so the browser's selection highlight skips it and a dragged
 * range, a select-all or a cell selection shows no sign of including a chip; the chip CSS paints
 * the selected-node class instead. ProseMirror sets that class only for a node selection, and
 * Tiptap's React node views mark themselves selected only for a node selection or a selection
 * inside them, never for one that spans them. A chip that is not `selectable` can only ever be in
 * a range selection, so without this it is never shown as selected at all.
 */
export function coveredAtomSelectionPlugin(typeName: string): Plugin {
  return new Plugin({
    key: new PluginKey(`${typeName}CoveredAtomSelection`),
    props: {
      decorations(state) {
        const { selection } = state;
        if (selection.empty || selection instanceof NodeSelection) return null;

        const decorations: Decoration[] = [];
        for (const { $from, $to } of selection.ranges) {
          state.doc.nodesBetween($from.pos, $to.pos, (node, pos) => {
            if (node.type.name !== typeName) return;
            if (pos >= $from.pos && pos + node.nodeSize <= $to.pos) {
              decorations.push(
                Decoration.node(pos, pos + node.nodeSize, { class: SELECTED_NODE_CLASS })
              );
            }
          });
        }
        return decorations.length > 0 ? DecorationSet.create(state.doc, decorations) : null;
      },
    },
  });
}
